import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PageHeader, EmptyState } from "@/components/ui-bits";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Upload, FileSpreadsheet, Download, CheckCircle2, AlertCircle, Eye, Search } from "lucide-react";
import { toast } from "sonner";
import { ROLE_LABELS } from "@/lib/roles";
import * as XLSX from "xlsx";

const roleKeys = Object.keys(ROLE_LABELS);
const empty = { full_name: "", employee_no: "", role: "teacher", designation: "", department: "", email: "", phone: "", salary: "" };

const TEMPLATE_COLUMNS = [
  "full_name", "title", "gender", "date_of_birth", "employee_no", "role",
  "designation", "department", "job_section", "employment_type",
  "email", "phone", "phone_alt", "address",
  "nationality", "state_of_origin", "lga", "religion", "marital_status",
  "nok_name", "nok_relationship", "nok_phone", "nok_address",
  "date_first_appointment", "date_assumption_of_duty", "date_of_joining", "salary",
  "bank_name", "bank_account_number", "bank_account_name", "bvn",
];

const TEMPLATE_SAMPLE = [
  { full_name: "Aisha Hassan",     title: "Mrs",  gender: "female", date_of_birth: "1990-05-12", employee_no: "DFA-STF-101", role: "teacher",     designation: "Math Teacher",   department: "Sciences", job_section: "Academic",   employment_type: "Full-time", email: "aisha@dfa.com.ng",  phone: "+234 803 100 0011", phone_alt: "",              address: "Zaria",  nationality: "Nigerian", state_of_origin: "Kaduna", lga: "Zaria",   religion: "Islam", marital_status: "Married", nok_name: "Hassan Ali", nok_relationship: "Husband", nok_phone: "+234 803 100 0021", nok_address: "Zaria", date_first_appointment: "2024-09-01", date_assumption_of_duty: "2024-09-15", date_of_joining: "2024-09-15", salary: 180000, bank_name: "Access Bank",   bank_account_number: "0123456789", bank_account_name: "Aisha Hassan",   bvn: "" },
  { full_name: "Yusuf Garba",      title: "Mallam", gender: "male", date_of_birth: "1985-11-03", employee_no: "",            role: "accountant",  designation: "Senior Accountant", department: "Finance", job_section: "Admin", employment_type: "Full-time", email: "yusuf@dfa.com.ng",  phone: "+234 803 100 0012", phone_alt: "",              address: "Zaria",  nationality: "Nigerian", state_of_origin: "Kaduna", lga: "Zaria",   religion: "Islam", marital_status: "Married", nok_name: "Fatima Garba", nok_relationship: "Wife",  nok_phone: "+234 803 100 0022", nok_address: "Zaria", date_first_appointment: "2023-01-10", date_assumption_of_duty: "2023-01-15", date_of_joining: "2023-01-15", salary: 220000, bank_name: "Zenith Bank",   bank_account_number: "1023456789", bank_account_name: "Yusuf Garba",    bvn: "" },
  { full_name: "Hadiza Suleiman",  title: "Hajiya", gender: "female", date_of_birth: "1992-02-20", employee_no: "",            role: "receptionist","designation": "Front Desk",  department: "Admin",  job_section: "Admin", employment_type: "Full-time", email: "hadiza@dfa.com.ng", phone: "+234 803 100 0013", phone_alt: "",              address: "Zaria",  nationality: "Nigerian", state_of_origin: "Kaduna", lga: "Zaria",   religion: "Islam", marital_status: "Single",  nok_name: "Suleiman Adamu", nok_relationship: "Father", nok_phone: "+234 803 100 0023", nok_address: "Zaria", date_first_appointment: "2025-09-01", date_assumption_of_duty: "2025-09-05", date_of_joining: "2025-09-05", salary: 120000, bank_name: "GTBank",        bank_account_number: "0987654321", bank_account_name: "Hadiza Suleiman", bvn: "" },
];

const VALID_ROLES = ["super_admin", "director", "teacher", "accountant", "receptionist", "attendance_officer", "inventory_officer"];

const Staff = () => {
  const { user } = useAuth();
  const effectiveCapabilities = user?.effective_capabilities || [];
  const userRoles = user?.roles || (user?.role ? [user.role] : []);
  const isSuperAdmin = userRoles.includes("super_admin");
  const hasEffectiveCapability = (capability) =>
    isSuperAdmin || effectiveCapabilities.includes(capability);
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [parsedRows, setParsedRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const canManage =
    hasEffectiveCapability("staff.manage");

  const load = async () => {
    try { const { data } = await api.get("/staff", { params: q ? { q } : {} }); setList(data); } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    try {
      const payload = { ...form, salary: form.salary ? Number(form.salary) : undefined };
      const { data } = await api.post("/staff", payload);
      toast.success(`Added ${data.full_name}`);
      setList((l) => [data, ...l]); setOpen(false); setForm(empty);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove staff member?")) return;
    try { await api.delete(`/staff/${id}`); setList((l) => l.filter((s) => s.id !== id)); toast.success("Removed"); } catch (e) { toast.error(formatApiError(e)); }
  };

  // -------- Bulk upload helpers --------
  const downloadTemplate = (format) => {
    const ws = XLSX.utils.json_to_sheet(TEMPLATE_SAMPLE, { header: TEMPLATE_COLUMNS });
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Staff Template");
    if (format === "csv") {
      const csv = XLSX.utils.sheet_to_csv(ws);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      triggerDownload(blob, "dfa_staff_template.csv");
    } else {
      const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([out], { type: "application/octet-stream" });
      triggerDownload(blob, "dfa_staff_template.xlsx");
    }
  };

  const triggerDownload = (blob, name) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = name; a.click();
    URL.revokeObjectURL(url);
  };

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
      const cleaned = rows.map((r) => {
        const norm = {};
        Object.keys(r).forEach((k) => {
          norm[String(k).trim().toLowerCase().replace(/\s+/g, "_")] = typeof r[k] === "string" ? r[k].trim() : r[k];
        });
        const full_name = (norm.full_name || norm.name || "").toString().trim();
        const role = (norm.role || "teacher").toString().trim().toLowerCase().replace(/\s+/g, "_");
        const reasons = [];
        if (!full_name) reasons.push("Missing full_name");
        if (!VALID_ROLES.includes(role)) reasons.push(`Invalid role '${role}'`);
        return { ...norm, full_name, role, _valid: reasons.length === 0, _reason: reasons.join(" · ") };
      });
      setParsedRows(cleaned);
      const valid = cleaned.filter((r) => r._valid).length;
      toast.success(`Parsed ${cleaned.length} rows · ${valid} valid`);
    } catch (e) {
      toast.error("Failed to parse file. Please check format.");
      setParsedRows([]);
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    handleFile(f);
    e.target.value = ""; // allow re-selecting same file
  };

  const onDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  };

  const submitBulk = async () => {
    const validRows = parsedRows.filter((r) => r._valid).map((r) => {
      const { _valid, _reason, ...rest } = r;  // strip internal flags
      return rest;
    });
    if (validRows.length === 0) return toast.error("No valid rows to upload");
    setUploading(true);
    try {
      const { data } = await api.post("/staff/bulk", { rows: validRows });
      setResult(data);
      toast.success(`Imported ${data.created_count} staff · ${data.skipped_count} skipped`);
      if (data.created?.length) setList((l) => [...data.created, ...l]);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const closeBulk = () => {
    setBulkOpen(false);
    setParsedRows([]); setFileName(""); setResult(null);
  };

  const validCount = parsedRows.filter((r) => r._valid).length;
  const invalidCount = parsedRows.length - validCount;

  return (
    <div data-testid="staff-page">
      <PageHeader
        overline="Human Resources"
        title="Staff Directory"
        description="Teachers, administrators and support team — all centrally managed."
        actions={canManage && (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && load()}
                placeholder="Search name or employee no..."
                className="pl-9 h-10 w-64 bg-white"
                data-testid="staff-search"
              />
            </div>
            <Button variant="outline" onClick={() => setBulkOpen(true)} className="border-[#E2E8F0]" data-testid="bulk-upload-btn">
              <Upload className="w-4 h-4 mr-1.5" /> Bulk Upload
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="add-staff-btn"><Plus className="w-4 h-4 mr-1.5" /> Add Staff</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle className="font-display text-[#1B2C5A]">Add Staff Member</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <Fld label="Full Name"><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="staff-form-name" /></Fld>
                  <Fld label="Employee No."><Input value={form.employee_no} onChange={(e) => setForm({ ...form, employee_no: e.target.value })} placeholder="Auto" /></Fld>
                  <Fld label="Role">
                    <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                      <SelectTrigger data-testid="staff-form-role"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {roleKeys.filter(r => !["parent","student"].includes(r)).map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Fld>
                  <Fld label="Designation"><Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} /></Fld>
                  <Fld label="Department"><Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} /></Fld>
                  <Fld label="Email"><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Fld>
                  <Fld label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Fld>
                  <Fld label="Salary (₦)"><Input type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} /></Fld>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button onClick={submit} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="staff-form-submit">Add Staff</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </>
        )}
      />

      {list.length === 0 ? <EmptyState message="No staff yet." testId="staff-empty" /> : (
        <Card className="border-[#E2E8F0] overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#FDFBF7]">
                <TableHead className="w-12"></TableHead>
                <TableHead>Emp. No.</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Designation</TableHead>
                <TableHead>Department</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {list.map(s => (
                <TableRow key={s.id} data-testid={`staff-row-${s.id}`}>
                  <TableCell>
                    <div className="w-9 h-9 rounded-full overflow-hidden bg-[#1B2C5A]/5 grid place-items-center text-[#1B2C5A] font-display text-xs">
                      {s.passport_photo ? <img src={s.passport_photo} alt="" className="w-full h-full object-cover" /> : (s.full_name||"").split(" ").slice(0,2).map(p=>p[0]).join("")}
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{s.employee_no}</TableCell>
                  <TableCell className="font-medium text-[#1B2C5A]">{s.full_name}</TableCell>
                  <TableCell>{ROLE_LABELS[s.role] || s.role}</TableCell>
                  <TableCell>{s.designation || "—"}</TableCell>
                  <TableCell>{s.department || "—"}</TableCell>
                  <TableCell>{s.phone || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/app/staff/${s.id}`)} className="border-[#E2E8F0]" data-testid={`staff-view-${s.id}`}>
                        <Eye className="w-3.5 h-3.5 mr-1" /> Profile
                      </Button>
                      {canManage && <Button size="sm" variant="ghost" className="text-red-700" onClick={() => remove(s.id)} data-testid={`staff-delete-${s.id}`}><Trash2 className="w-4 h-4" /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkOpen} onOpenChange={(v) => v ? setBulkOpen(true) : closeBulk()}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-[#1B2C5A]">Bulk Upload Staff</DialogTitle>
            <DialogDescription className="text-[#475569]">
              Upload a CSV or Excel file. Required column: <code className="text-[#1B2C5A]">full_name</code>. Recommended: <code className="text-[#1B2C5A]">role</code> (defaults to <em>teacher</em>).
            </DialogDescription>
          </DialogHeader>

          {/* Templates */}
          <div className="flex flex-wrap gap-2 mt-1">
            <Button variant="outline" size="sm" onClick={() => downloadTemplate("xlsx")} className="border-[#E2E8F0]" data-testid="download-xlsx-template">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download Excel Template
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadTemplate("csv")} className="border-[#E2E8F0]" data-testid="download-csv-template">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Download CSV Template
            </Button>
            <div className="text-xs text-[#475569] ml-auto flex items-center">
              Valid roles: {VALID_ROLES.join(", ")}
            </div>
          </div>

          {/* Drop zone */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            className="mt-2 rounded-xl border-2 border-dashed border-[#E2E8F0] hover:border-[#9DCD3F] bg-[#FDFBF7] p-8 text-center cursor-pointer transition-colors"
            data-testid="bulk-dropzone"
          >
            <FileSpreadsheet className="w-10 h-10 mx-auto text-[#9DCD3F]" />
            <div className="mt-3 font-display text-lg text-[#1B2C5A]">
              {fileName ? fileName : "Drop CSV/Excel file or click to browse"}
            </div>
            <div className="text-xs text-[#475569] mt-1">Accepts .csv, .xlsx, .xls — up to 1000 rows</div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/csv"
              className="hidden"
              onChange={onPickFile}
              data-testid="bulk-file-input"
            />
          </div>

          {/* Preview */}
          {parsedRows.length > 0 && (
            <div className="mt-2">
              <div className="flex items-center gap-4 text-sm mb-2">
                <span className="inline-flex items-center gap-1.5 text-emerald-700"><CheckCircle2 className="w-4 h-4" /> {validCount} valid</span>
                {invalidCount > 0 && <span className="inline-flex items-center gap-1.5 text-red-700"><AlertCircle className="w-4 h-4" /> {invalidCount} invalid</span>}
                <span className="ml-auto text-[#475569] text-xs">{parsedRows.length} total rows</span>
              </div>
              <div className="border border-[#E2E8F0] rounded-lg overflow-hidden max-h-72 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-[#FDFBF7] sticky top-0">
                      <TableHead className="w-10">#</TableHead>
                      <TableHead>Full Name</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Designation</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {parsedRows.map((r, i) => (
                      <TableRow key={i} className={r._valid ? "" : "bg-red-50/40"} data-testid={`preview-row-${i}`}>
                        <TableCell className="text-xs text-[#475569]">{i + 1}</TableCell>
                        <TableCell className="font-medium text-[#1B2C5A]">{r.full_name || "—"}</TableCell>
                        <TableCell>{r.role}</TableCell>
                        <TableCell>{r.designation || "—"}</TableCell>
                        <TableCell>{r.email || "—"}</TableCell>
                        <TableCell>{r.phone || "—"}</TableCell>
                        <TableCell>
                          {r._valid
                            ? <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Ready</span>
                            : <span className="text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200" title={r._reason}>{r._reason}</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* Result */}
          {result && (
            <div className="mt-3 p-4 rounded-lg bg-[#9DCD3F]/10 border border-[#9DCD3F]/40">
              <div className="font-display text-[#1B2C5A]">Import complete</div>
              <div className="text-sm text-[#475569] mt-1">
                ✅ Created: <strong>{result.created_count}</strong> · ⚠️ Skipped: <strong>{result.skipped_count}</strong>
              </div>
              {result.skipped?.length > 0 && (
                <ul className="mt-2 text-xs text-red-700 space-y-0.5 max-h-24 overflow-y-auto">
                  {result.skipped.map((s, i) => <li key={i}>Row {s.row}: {s.reason}</li>)}
                </ul>
              )}
            </div>
          )}

          <DialogFooter className="mt-2">
            <Button variant="outline" onClick={closeBulk}>Close</Button>
            <Button
              onClick={submitBulk}
              disabled={validCount === 0 || uploading}
              className="bg-[#1B2C5A] hover:bg-[#14224A] text-white"
              data-testid="bulk-submit"
            >
              <Upload className="w-4 h-4 mr-1.5" />
              {uploading ? "Uploading..." : `Import ${validCount} staff`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Fld = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[#1B2C5A] text-sm">{label}</Label>
    {children}
  </div>
);

export default Staff;
